<script setup lang="ts">
import { ref, computed } from 'vue'
import { Folder, useFilingStore } from '@/modules/filing/stores/filing-store'
import { sortItems } from '@/modules/filing/utils/utils'
import VFolderTree from './VFolderTree.vue'
import VFile from './VFile.vue'
import VFolder from './VFolder.vue'

const props = withDefaults(
  defineProps<{
    folder?: Folder
    isReadOnly?: boolean
    level?: number
    loadFolderContents: Function
    openFolder: Function
    onFileDragStart: Function
    onFolderDragStart: Function
    addFolder: Function
    editFolder: Function
    onItemDrop: Function
    onSelectRow: Function
    selectedKeys: string[]
    sortState: { column: string; direction: string }
  }>(),
  {
    folder: null,
    level: 0,
  },
)

const filingStore = useFilingStore()
const isExpanded = ref(props.level === 0)

const childFolders = computed(() => {
  const folders = filingStore.folders.filter((f) => f.parent_id === (props.folder?.id || null))
  return sortItems(folders, props.sortState)
})

const childFiles = computed(() => {
  const files = filingStore.files.filter((f) => f.filing_folder_id === (props.folder?.id || null))
  return sortItems(files, props.sortState)
})

const toggleExpand = async () => {
  isExpanded.value = !isExpanded.value

  if (isExpanded.value) {
    await props.loadFolderContents(props.folder?.id)
  }
}
</script>

<template>
  <div class="v-folder-tree">
    <TransitionGroup name="slide-fade">
      <VFolder
        :key="'folder-' + (props.folder?.id || 'root')"
        :folder="props.folder"
        :level="props.level"
        :isExpanded="isExpanded"
        :toggleExpand="toggleExpand"
        :openFolder="props.openFolder"
        :addFolder="addFolder"
        :editFolder="editFolder"
        :onFolderDragStart="props.onFolderDragStart"
        :onItemDrop="props.onItemDrop"
        :onSelect="props.onSelectRow"
        :selectedKeys="props.selectedKeys"
        :isReadOnly="props.isReadOnly"
        v-if="props.level > 0"
      />
      <template v-if="isExpanded">
        <VFolderTree
          v-for="child in childFolders"
          :key="'folder-' + child.id"
          :folder="child"
          :level="props.level + 1"
          :loadFolderContents="loadFolderContents"
          :openFolder="openFolder"
          :addFolder="addFolder"
          :editFolder="editFolder"
          :onItemDrop="props.onItemDrop"
          :onFolderDragStart="props.onFolderDragStart"
          :onFileDragStart="props.onFileDragStart"
          :onSelectRow="props.onSelectRow"
          :selectedKeys="props.selectedKeys"
          :sortState="props.sortState"
          :isReadOnly="props.isReadOnly"
        />
        <VFile
          v-for="file in childFiles"
          :key="'file-' + file.id"
          :file="file"
          :level="props.level"
          :onFileDragStart="props.onFileDragStart"
          :onItemDrop="props.onItemDrop"
          :onSelect="props.onSelectRow"
          :selectedKeys="props.selectedKeys"
          :isReadOnly="props.isReadOnly"
        />
      </template>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.1s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.1s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.v-folder-tree {
  @apply col-span-5; /* Ensure the wrapper spans all 5 columns */
}
</style>
